<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import useUpdateDataPointCustomCategoryNameMutation from '@/api/mutations/DataPointCategory/updateDataPointCustomCategoryName.mutation';
import AtButton from '@/components/atoms/AtButton/AtButton.vue';
import AtInput from '@/components/atoms/AtInput/AtInput.vue';

type Category = {
  _id: string;
  name: string;
};

const props = defineProps<{
  category: Category;
  existingCategoryNames: string[];
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();

const { mutate } = useUpdateDataPointCustomCategoryNameMutation({
  update: (store) => {
    store.evict({
      id: 'ROOT_QUERY',
      fieldName: 'getCategoriesWithSubcategories',
    });
    store.evict({
      id: 'ROOT_QUERY',
      fieldName: 'getAllCategoriesForEntity',
    });
  },
});

const form = reactive({
  name: '',
});
watch(
  () => props.category.name,
  (newCategoryName) => {
    form.name = newCategoryName;
  },
  { immediate: true },
);

const validationRules = computed(() => {
  const existingNames = new Set(props.existingCategoryNames);

  return {
    name: {
      required: helpers.withMessage(t('Category name is required.'), required),
      unique: helpers.withMessage(
        t('A category with this name already exists.'),
        (name: string) => !existingNames.has(name),
      ),
    },
  };
});
const v$ = useVuelidate(validationRules, form);

const formRef = ref<HTMLElement>();
onClickOutside(formRef, handleClose);

function handleSubmit() {
  mutate({
    input: {
      _id: props.category._id,
      name: form.name.trim(),
    },
  });
  handleClose();
}

function handleClose() {
  emit('close');
}
</script>

<template>
  <form ref="formRef" class="inline-flex gap-2" @submit.prevent="handleSubmit">
    <AtInput
      v-model="form.name"
      :errors="v$.name.$errors"
      @blur="v$.name.$touch"
    />
    <AtButton class="self-start" :disabled="v$.$invalid">
      {{ t('Apply') }}
    </AtButton>
    <AtButton
      class="self-start"
      variant="text"
      type="button"
      @click="handleClose"
    >
      {{ t('Cancel') }}
    </AtButton>
  </form>
</template>
